export const ALLOWED_TAGS = [
  'p', 'h2', 'h3', 'ul', 'ol', 'li', 'blockquote',
  'strong', 'em', 'a', 'hr', 'br', 'img'
];

export const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  'a': ['href', 'title', 'target', 'rel'],
  'img': ['src', 'alt', 'title']
};

export function sanitizeHTML(html: string): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  function cleanNode(node: Node): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.cloneNode(true);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (!ALLOWED_TAGS.includes(tagName)) {
      const fragment = document.createDocumentFragment();
      Array.from(element.childNodes).forEach(child => {
        const cleaned = cleanNode(child);
        if (cleaned) fragment.appendChild(cleaned);
      });
      return fragment;
    }

    const cleanElement = document.createElement(tagName);

    const allowedAttrs = ALLOWED_ATTRIBUTES[tagName] || [];
    allowedAttrs.forEach(attr => {
      const value = element.getAttribute(attr);
      if (value) {
        if (attr === 'href' && !value.match(/^(https?:\/\/|mailto:|tel:|#)/)) {
          return;
        }
        cleanElement.setAttribute(attr, value);
      }
    });

    if (tagName === 'a' && !cleanElement.hasAttribute('rel')) {
      cleanElement.setAttribute('rel', 'noopener noreferrer');
    }

    Array.from(element.childNodes).forEach(child => {
      const cleaned = cleanNode(child);
      if (cleaned) cleanElement.appendChild(cleaned);
    });

    return cleanElement;
  }

  const fragment = document.createDocumentFragment();
  Array.from(tempDiv.childNodes).forEach(child => {
    const cleaned = cleanNode(child);
    if (cleaned) fragment.appendChild(cleaned);
  });

  const resultDiv = document.createElement('div');
  resultDiv.appendChild(fragment);
  return resultDiv.innerHTML;
}

export function stripAllFormatting(html: string): string {
  const sanitized = sanitizeHTML(html);

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitized;

  const elements = tempDiv.querySelectorAll('*');
  elements.forEach(el => {
    el.removeAttribute('style');
    el.removeAttribute('class');
    if (el.tagName.toLowerCase() === 'span') {
      while (el.firstChild) {
        el.parentNode?.insertBefore(el.firstChild, el);
      }
      el.remove();
    }
  });

  return tempDiv.innerHTML;
}

export function normalizeListFormatting(html: string): string {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const lists = tempDiv.querySelectorAll('ul, ol');
  lists.forEach(list => {
    list.removeAttribute('style');
    list.removeAttribute('class');

    const items = list.querySelectorAll('li');
    items.forEach(item => {
      item.removeAttribute('style');
      item.removeAttribute('class');
    });
  });

  return tempDiv.innerHTML;
}
